import { TIconName } from '@unmind/design-system';
import RoutePath from 'App/RoutePath';
import { CustomTypeOptions } from 'react-i18next';
import { OnboardingSupportOption } from '__generated__/globalTypes';
import contentImage from './assets/welcome-modal-content.jpg';
import immediateSupportImage from './assets/welcome-modal-immediate-support.jpg';
import managersImage from './assets/welcome-modal-managers.jpg';
import novaImage from './assets/welcome-modal-nova.jpg';
import practicalSupportImage from './assets/welcome-modal-practical-support.jpg';
import therapyCoachingImage from './assets/welcome-modal-therapy-coaching.jpg';

/** `not_sure` is a row on the question view, not an offering. */
type SupportOptionTranslationKey = Exclude<
  keyof CustomTypeOptions['resources']['homepage']['welcome_modal']['options'],
  'not_sure'
>;

type DetailBlocks =
  CustomTypeOptions['resources']['homepage']['welcome_modal']['details'];

/**
 * Derived per offering, because they have different numbers of rows — a shared
 * row count would type keys that do not exist.
 */
type DetailRowKey = {
  [K in SupportOptionTranslationKey]: `details.${K}.${Extract<
    keyof DetailBlocks[K],
    `row${number}`
  >}`;
}[SupportOptionTranslationKey];

export interface DetailRow {
  icon: TIconName;
  textKey: DetailRowKey;
}

export interface SupportOptionConfig {
  /** Key under `homepage:welcome_modal.options`, and under `.details`. */
  translationKey: SupportOptionTranslationKey;
  icon: TIconName;
  detailRows: DetailRow[];
  /** Aspect ratios vary between offerings, so render with `object-fit: cover`. */
  detailImage: string;
  /**
   * Where the call to action goes. Absent for practical support, which resolves
   * entirely from the member's own services — use `getCtaDestination`.
   */
  ctaDestination?: string;
}

/**
 * Everything the modal knows about an offering, keyed by the value
 * `onboardingSupportOptions` returns.
 *
 * Eligibility is deliberately absent: the server decides what a member sees.
 */
export const SUPPORT_OPTIONS: Partial<
  Record<OnboardingSupportOption, SupportOptionConfig>
> = {
  [OnboardingSupportOption.NOVA]: {
    translationKey: 'nova',
    icon: 'message-sparkle',
    detailRows: [
      { icon: 'message-heart', textKey: 'details.nova.row1' },
      { icon: 'badge-check', textKey: 'details.nova.row2' },
      { icon: 'route', textKey: 'details.nova.row3' },
      { icon: 'user-sparkle', textKey: 'details.nova.row4' },
      { icon: 'eye-slash', textKey: 'details.nova.row5' },
    ],
    detailImage: novaImage,
    ctaDestination: RoutePath.Chatbot,
  },
  [OnboardingSupportOption.THERAPY_COACHING]: {
    translationKey: 'therapy_coaching',
    icon: 'calendar-heart',
    detailRows: [
      { icon: 'instant-book', textKey: 'details.therapy_coaching.row1' },
      { icon: 'heart-handshake', textKey: 'details.therapy_coaching.row2' },
      { icon: 'coaching', textKey: 'details.therapy_coaching.row3' },
      { icon: 'language', textKey: 'details.therapy_coaching.row4' },
      { icon: 'eye-slash', textKey: 'details.therapy_coaching.row5' },
    ],
    detailImage: therapyCoachingImage,
    ctaDestination: RoutePath.Book,
  },
  [OnboardingSupportOption.CONTENT]: {
    translationKey: 'content',
    icon: 'playlist',
    detailRows: [
      { icon: 'headphones', textKey: 'details.content.row1' },
      { icon: 'practical-tools', textKey: 'details.content.row2' },
      { icon: 'presentation-screen', textKey: 'details.content.row3' },
      { icon: 'book', textKey: 'details.content.row4' },
      { icon: 'eye-slash', textKey: 'details.content.row5' },
    ],
    detailImage: contentImage,
    ctaDestination: RoutePath.Explore,
  },
  [OnboardingSupportOption.MANAGERS]: {
    translationKey: 'managers',
    icon: 'manager',
    detailRows: [
      { icon: 'graduation-cap', textKey: 'details.managers.row1' },
      { icon: 'practical-tools', textKey: 'details.managers.row2' },
      { icon: 'media-library', textKey: 'details.managers.row3' },
      { icon: 'practical-support', textKey: 'details.managers.row4' },
    ],
    detailImage: managersImage,
    ctaDestination: RoutePath.Managers,
  },
  [OnboardingSupportOption.PRACTICAL_SUPPORT]: {
    translationKey: 'practical_support',
    icon: 'practical-support',
    detailRows: [
      { icon: 'calendar-book', textKey: 'details.practical_support.row1' },
      { icon: 'legal-support', textKey: 'details.practical_support.row2' },
      { icon: 'baby-carriage', textKey: 'details.practical_support.row3' },
      { icon: 'hand-holding-heart', textKey: 'details.practical_support.row4' },
      { icon: 'eye-slash', textKey: 'details.practical_support.row5' },
    ],
    detailImage: practicalSupportImage,
  },
  [OnboardingSupportOption.IMMEDIATE_SUPPORT]: {
    translationKey: 'immediate_support',
    icon: 'call',
    detailRows: [
      { icon: 'call', textKey: 'details.immediate_support.row1' },
      { icon: 'hand-holding-heart', textKey: 'details.immediate_support.row2' },
      { icon: 'language', textKey: 'details.immediate_support.row3' },
      { icon: 'route', textKey: 'details.immediate_support.row4' },
      { icon: 'eye-slash', textKey: 'details.immediate_support.row5' },
    ],
    detailImage: immediateSupportImage,
    ctaDestination: RoutePath.Help,
  },
};

/** Returns undefined for offerings this build does not recognise: Apollo does
 * not check enum values at runtime. */
export const getSupportOptionConfig = (
  option: OnboardingSupportOption,
): SupportOptionConfig | undefined => SUPPORT_OPTIONS[option];

/**
 * Fewest offerings worth asking about. A member entitled to one thing is asked
 * to choose between it and "I'm not sure yet", which is not a question — send
 * them to the homepage instead.
 */
export const MINIMUM_SUPPORT_OPTIONS = 2;

/**
 * The offerings the modal can describe. The views skip any they cannot, so
 * count only these rather than rows that would never render.
 */
export const getDescribableOptions = (
  options: readonly OnboardingSupportOption[],
): OnboardingSupportOption[] =>
  options.filter(option => getSupportOptionConfig(option) !== undefined);
